import React from "react";
import { Control, UseFormSetValue } from "react-hook-form";

import SelectField, { SelectOptionType } from "@/components/SelectField";
import { PAGINATION } from "@/constants/common";
import {
  optionsAgeBand,
  optionsChannelPropensity,
  optionsCurrentMonthBounce,
  optionsCustomerRiskProfile,
  optionsOccupation,
  optionsOnlinePaidEver,
  optionsPaidLast3Month,
  optionsPropensityToPay,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import {
  Area,
  City,
  Pincode,
  State,
  useLocationMaster,
} from "@/services/hooks/customer-params/useCustomerParams";
import useCampaignStore from "@/store/campaignStore";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { Flex, Title } from "../../styles";

interface CustomerParametersProps {
  isEdit: boolean;
  control: Control<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
}

const transformLocationToOptions = (
  locations: Array<{ id: number; name?: string; pincode?: string }> | undefined
): SelectOptionType[] => {
  if (!locations) return [];
  return locations.map((loc) => ({
    label: loc.pincode || loc.name || "",
    value: loc.id.toString(),
  }));
};

export const CustomerParameters = ({
  isEdit,
  control,
  setValue,
}: CustomerParametersProps) => {
  const { customerParametersOptions: customerParametersOptionsStore } =
    useCampaignStore();

  const [selectedStateIds, setSelectedStateIds] = useState<string[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);

  const watchedState = useWatch({ control, name: "customer_state" });
  const watchedCity = useWatch({ control, name: "customer_city" });
  const watchedArea = useWatch({ control, name: "customer_area" });

  useEffect(() => {
    if (watchedState && Array.isArray(watchedState)) {
      setSelectedStateIds(watchedState.map((s) => s.value as string));
    } else if (!watchedState) {
      setSelectedStateIds([]);
    }
  }, [watchedState]);

  useEffect(() => {
    if (watchedCity && Array.isArray(watchedCity)) {
      setSelectedCityIds(watchedCity.map((c) => c.value as string));
    } else if (!watchedCity) {
      setSelectedCityIds([]);
    }
  }, [watchedCity]);

  useEffect(() => {
    if (watchedArea && Array.isArray(watchedArea)) {
      setSelectedAreaIds(watchedArea.map((a) => a.value as string));
    } else if (!watchedArea) {
      setSelectedAreaIds([]);
    }
  }, [watchedArea]);

  const { data: statesData } = useLocationMaster<State>(
    { type: "state", limit: 1000 },
    { enabled: true }
  );
  const stateOptions = useMemo(
    () => transformLocationToOptions(statesData),
    [statesData]
  );

  const { data: citiesData } = useLocationMaster<City>(
    {
      type: "city",
      parent_id: selectedStateIds,
      limit: PAGINATION.MAX_LIMIT,
    },
    { enabled: selectedStateIds.length > 0 }
  );
  const cityOptions = useMemo(
    () => transformLocationToOptions(citiesData),
    [citiesData]
  );

  const { data: areasData } = useLocationMaster<Area>(
    {
      type: "area",
      parent_id: selectedCityIds,
      limit: PAGINATION.MAX_LIMIT,
    },
    { enabled: selectedCityIds.length > 0 }
  );
  const areaOptions = useMemo(
    () => transformLocationToOptions(areasData),
    [areasData]
  );

  const { data: pincodesData } = useLocationMaster<Pincode>(
    {
      type: "pincode",
      parent_id: selectedAreaIds,
      limit: PAGINATION.MAX_LIMIT,
    },
    { enabled: selectedAreaIds.length > 0 }
  );
  const pincodeOptions = useMemo(
    () => transformLocationToOptions(pincodesData),
    [pincodesData]
  );

  const handleStateChange = (selectedOptions: SelectOptionType[] | null) => {
    const newSelectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value as string)
      : [];
    setSelectedStateIds(newSelectedIds);
    setValue("customer_city", [], { shouldValidate: true });
    setValue("customer_area", [], { shouldValidate: true });
    setValue("customer_pincode", [], { shouldValidate: true });
  };

  const handleCityChange = (selectedOptions: SelectOptionType[] | null) => {
    const newSelectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value as string)
      : [];
    setSelectedCityIds(newSelectedIds);
    setValue("customer_area", [], { shouldValidate: true });
    setValue("customer_pincode", [], { shouldValidate: true });
  };

  const handleAreaChange = (selectedOptions: SelectOptionType[] | null) => {
    const newSelectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value as string)
      : [];
    setSelectedAreaIds(newSelectedIds);
    setValue("customer_pincode", [], { shouldValidate: true });
  };

  useEffect(() => {
    if (isEdit && customerParametersOptionsStore) {
      setValue("customer_state", customerParametersOptionsStore.optionsState);
      setValue("customer_city", customerParametersOptionsStore.optionsCity);
      setValue("customer_area", customerParametersOptionsStore.optionsArea);
      setValue(
        "customer_pincode",
        customerParametersOptionsStore.optionsPincode
      );
    }
  }, [isEdit, setValue, customerParametersOptionsStore]);

  return (
    <React.Fragment>
      <Title>Customer Parameters</Title>
      <Flex>
        {/* Customer Region will disable when api is ready */}
        <SelectField
          name="customer_region"
          label="Customer Region"
          placeholder="Select Customer Region"
          isMultiple
          withCheckbox
          disabled
          control={control}
          options={[]}
        />
        <SelectField
          name="customer_state"
          label="Customer State"
          placeholder="Select Customer State"
          // isRequired
          isMultiple
          withCheckbox
          withSearch
          control={control}
          options={
            isEdit ? customerParametersOptionsStore?.optionsState : stateOptions
          }
          onChange={(selected) =>
            handleStateChange(selected as SelectOptionType[] | null)
          }
          disabled={isEdit}
        />
        <SelectField
          name="customer_city"
          label="Customer City"
          placeholder="Select Customer City"
          // isRequired
          isMultiple
          withCheckbox
          withSearch
          control={control}
          options={
            isEdit ? customerParametersOptionsStore?.optionsCity : cityOptions
          }
          disabled={selectedStateIds.length === 0 || isEdit}
          onChange={(selected) =>
            handleCityChange(selected as SelectOptionType[] | null)
          }
        />
        <SelectField
          name="customer_area"
          label="Customer Area"
          placeholder="Select Customer Area"
          // isRequired
          isMultiple
          withCheckbox
          withSearch
          control={control}
          options={
            isEdit ? customerParametersOptionsStore?.optionsArea : areaOptions
          }
          disabled={selectedCityIds.length === 0 || isEdit}
          onChange={(selected) =>
            handleAreaChange(selected as SelectOptionType[] | null)
          }
        />
        <SelectField
          name="customer_pincode"
          label="Customer Pincode"
          placeholder="Select Customer Pincode"
          isMultiple
          withCheckbox
          withSearch
          control={control}
          options={
            isEdit
              ? customerParametersOptionsStore?.optionsPincode
              : pincodeOptions
          }
          disabled={selectedAreaIds.length === 0 || isEdit}
        />
        <SelectField
          name="occupation"
          label="Occupation"
          placeholder="Select Occupation"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsOccupation}
          disabled={isEdit}
        />
        <SelectField
          name="age_band"
          label="Age Band"
          placeholder="Select Age Band"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsAgeBand}
          disabled={isEdit}
        />
        <SelectField
          name="customer_risk_profile"
          label="All Customer Risk Profile"
          placeholder="Select Customer Risk Profile"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsCustomerRiskProfile}
          disabled={isEdit}
        />
        <SelectField
          name="channel_propensity"
          label="Channel Propensity"
          placeholder="Select Channel Propensity"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsChannelPropensity}
          disabled={isEdit}
        />
        <SelectField
          name="propensity_to_pay"
          label="Propensity To Pay"
          placeholder="Select Propensity To Pay"
          // isRequired
          isMultiple
          withCheckbox
          control={control}
          options={optionsPropensityToPay}
          disabled={isEdit}
        />
        <SelectField
          name="paid_last_three_months"
          label="Paid Last 3 Months"
          placeholder="Select Paid Last 3 Months"
          // isRequired
          control={control}
          options={optionsPaidLast3Month}
          disabled={isEdit}
        />
        <SelectField
          name="online_paid_ever"
          label="Online Paid Ever"
          placeholder="Select Online Paid Ever"
          // isRequired
          control={control}
          options={optionsOnlinePaidEver}
          disabled={isEdit}
        />
        <SelectField
          name="current_month_bounce"
          label="Current Month Bounce"
          placeholder="Select Current Month Bounce"
          // isRequired
          control={control}
          options={optionsCurrentMonthBounce}
          disabled={isEdit}
        />
      </Flex>
    </React.Fragment>
  );
};
export default CustomerParameters;
